import type { TierBarSegment } from "../types";

// Segment fill per tone — the /70 alpha-on-base-hue convention SectionTracker
// uses (the -bg tokens are too light for this dark surface).
const TONE_BG: Record<NonNullable<TierBarSegment["tone"]>, string> = {
  success: "bg-status-success/70",
  warning: "bg-status-warning/70",
  muted: "bg-muted/50",
};

interface TierBarProps {
  segments: TierBarSegment[];
  value: number;
  valueLabel?: string;
  target: number;
  unit?: string;
}

/** Segmented 100% composition bar with a reference tick and legend — the
 *  `tier_bar` section kind. Title renders in the enclosing shell/title row. */
export function TierBar({ segments, value, valueLabel, target, unit = "%" }: TierBarProps) {
  return (
    <div className="px-5 py-4 space-y-2">
      <div className="flex items-baseline justify-end">
        <span className="text-sm tabular-nums text-muted-foreground">
          {valueLabel ? <span>{valueLabel} </span> : null}
          <span className="text-foreground">
            {value.toLocaleString()}
            {unit}
          </span>
          <span className="px-2 text-muted-foreground/50">/</span>
          <span className="text-foreground">
            {target.toLocaleString()}
            {unit}
          </span>
        </span>
      </div>
      <div className="relative h-6 w-full rounded-sm bg-muted/10">
        <div className="absolute inset-0 flex overflow-hidden rounded-sm">
          {segments.map((s) => (
            <div
              key={s.label}
              className={TONE_BG[s.tone ?? "muted"]}
              style={{ width: `${s.pct}%` }}
              title={`${s.label}: ${s.count.toLocaleString()} (${s.pct}${unit})`}
            />
          ))}
        </div>
        {/* The tick is a query-computed reference (e.g. portfolio average) —
            never labelled "target": invented-target language is banned by the
            dashboard spec's binding data rules. */}
        <div
          className="absolute inset-y-0 w-0.5 bg-foreground"
          style={{ left: `calc(${target}% - 1px)` }}
          aria-label="Reference"
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {segments.map((s) => (
          <span key={s.label} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-[2px] ${TONE_BG[s.tone ?? "muted"]}`} />
            {s.label} {s.count.toLocaleString()} ({s.pct}
            {unit})
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-0.5 bg-foreground" />
          {target}
          {unit} reference
        </span>
      </div>
    </div>
  );
}
